import type { HelpArticle } from './types';

type Scored = { article: HelpArticle; score: number; matched: number; strong: number };

/**
 * Words carrying no signal about WHICH article is wanted.
 *
 * "mucka" is in here deliberately: every article is about Mucka, so leaving it
 * in scored a point against practically the whole knowledge base and let one
 * incidental word outrank a precise match. A user asking "how much does Mucka
 * cost" was landing on an invoice-numbering article, because that summary
 * happened to contain more filler words than the pricing one (TKT-1051).
 *
 * Keep this list to genuine filler. Anything a user might be searching FOR
 * ("cost", "price", "job", "quote") must stay out of it.
 */
const STOPWORDS = new Set<string>([
  // Short function words. Single characters are dropped separately by length.
  'am', 'an', 'as', 'at', 'be', 'by', 'do', 'if', 'in', 'is', 'it', 'me',
  'my', 'no', 'of', 'on', 'or', 'so', 'to', 'up', 'us', 'we',
  'the', 'and', 'for', 'with', 'from', 'that', 'this', 'these', 'those',
  'how', 'what', 'when', 'where', 'which', 'who', 'why', 'does', 'did',
  'can', 'could', 'would', 'should', 'will', 'shall', 'may', 'might',
  'you', 'your', 'yours', 'our', 'ours', 'its', 'their',
  'are', 'was', 'were', 'been', 'being', 'have', 'has', 'had',
  'about', 'into', 'onto', 'over', 'under', 'out', 'off',
  'much', 'many', 'any', 'some', 'all', 'get', 'got', 'set',
  'not', 'but', 'than', 'then', 'there', 'here',
  'mucka',
]);

/**
 * A hit must cover at least this share of the query's meaningful terms.
 *
 * Without it, one incidental substring match was enough to return an article:
 * "zzzqqqxx nonsense term" came back with a payment-terms article because
 * "term" is a substring of "terms". The engine then tried to answer from a
 * completely unrelated article rather than saying it didn't know.
 */
const MIN_TERM_COVERAGE = 0.5;

/**
 * Does `text` contain `term`, allowing for the ending having changed?
 *
 * Matching is substring-in-text, so it only ever forgave one direction: the
 * query word "sheet" is inside the title "job sheets", but the query word
 * "sheets" is not inside the title "Fill in a job sheet". People ask in the
 * plural ("how do I delete job sheets and job reports") and in the past tense
 * ("is a job sheet emailed"), and articles are titled in the singular and
 * keyworded in the present. TKT-1645: the job sheet article scored nothing
 * on "sheets" or "emailed" and lost the owner's questions to the job report
 * article, whose body then told Mucka nothing about job sheets.
 *
 * Trimming a short suffix off a long enough word is the whole of the stemming
 * needed. The five-character floor is what keeps it honest: "pricing" does not
 * become "pric", "booking" does not become "book" (#40 — a short word gets no
 * tolerance at all).
 */
export function mentions(text: string, term: string): boolean {
  if (text.includes(term)) return true;
  const stem = term.replace(/(?:ised|ized|ing|ed|es|s)$/, '');
  return stem.length >= 5 && stem !== term && text.includes(stem);
}

/** `mentions`, for an exact keyword: "job sheets" asked is "job sheet" filed. */
function sameWord(keyword: string, term: string): boolean {
  if (keyword === term) return true;
  const stem = term.replace(/(?:ised|ized|ing|ed|es|s)$/, '');
  return stem.length >= 5 && stem !== term && keyword === stem;
}

function scoreArticle(
  article: HelpArticle,
  terms: string[],
): { score: number; matched: number; strong: number } {
  let score = 0;
  let matched = 0;
  let strong = 0;
  const title = article.title.toLowerCase();
  const summary = article.summary.toLowerCase();
  const keywords = (article.keywords ?? []).map((k) => k.toLowerCase());

  for (const term of terms) {
    let termScore = 0;
    // Title and keyword hits are STRONG: both are deliberate, curated signals
    // about what an article is for. A summary hit is weak — summaries are
    // prose, so they collide with words that say nothing about the topic
    // ("cost of Mucka" was landing on a calendar article off its summary).
    let isStrong = false;
    if (mentions(title, term)) { termScore += 8; isStrong = true; }
    if (title.startsWith(term)) termScore += 4;
    if (keywords.some((k) => mentions(k, term))) { termScore += 5; isStrong = true; }
    // An exact keyword hit is the strongest signal we have — keywords are
    // hand-curated per article precisely to catch how users phrase things.
    if (keywords.some((k) => sameWord(k, term))) termScore += 6;
    if (mentions(summary, term)) termScore += 3;
    if (termScore > 0) matched++;
    if (isStrong) strong++;
    score += termScore;
  }

  // Reward breadth: an article matching two of the query's terms beats one
  // matching a single term very loudly. Without this, a title containing one
  // common word outranked a genuinely on-topic article.
  return { score: score * (1 + (matched - 1) * 0.5), matched, strong };
}

/**
 * The meaningful words in a query — the same tokenising and the same stopword
 * list `searchArticles` ranks on.
 *
 * Exported because `executeFindHelpArticle` has to decide which PART of a long
 * article to hand over, and it must make that decision on the same terms the
 * search made its own. Two tokenisers would drift, and the drift would be
 * invisible: an article correctly found, then clipped to the section that
 * happens to match a slightly different word list.
 */
export function queryTerms(query: string): string[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [];
  // De-duplicated: "delete job sheets and job reports" says "job" twice, and
  // counting it twice paid every article with "job" in it double and raised
  // the coverage bar to a word the query only meant once.
  return [...new Set(
    trimmed
      .split(/[^a-z0-9]+/)
      .filter(t => t.length > 1 && !STOPWORDS.has(t)),
  )];
}

/**
 * Every article that qualifies for `query`, best first, with its score.
 *
 * `searchArticles` is this without the scores. The engine's executor needs
 * them: whether the runner-up is a near tie decides whether it is handed over
 * as an article or as one line of metadata.
 */
export function rankArticles(
  query: string,
  articles: HelpArticle[],
): { article: HelpArticle; score: number }[] {
  // A query with no meaningful word left ("how much is Mucka?") has nothing to
  // rank on. Returning nothing is the honest answer — the caller then says so,
  // or answers from what it already knows. Falling back to the filler terms
  // just surfaced whichever articles happened to have "Mucka" in the title.
  const terms = queryTerms(query);
  if (terms.length === 0) return [];

  const required = Math.max(1, Math.ceil(terms.length * MIN_TERM_COVERAGE));

  return articles
    .map((a): Scored => ({ article: a, ...scoreArticle(a, terms) }))
    // `strong >= 1` is what stops a stray summary word qualifying an article
    // on its own — the difference between "no article covers this, say so"
    // and confidently answering from something unrelated.
    .filter((s) => s.score > 0 && s.matched >= required && s.strong >= 1)
    .sort((a, b) => b.score - a.score)
    .map(({ article, score }) => ({ article, score }));
}

export function searchArticles(
  query: string,
  articles: HelpArticle[],
  limit = 5,
): HelpArticle[] {
  return rankArticles(query, articles).slice(0, limit).map((s) => s.article);
}
